import React, { useState } from 'react';
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse, Box, Typography, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore, Description, Assessment, People, Settings, Feedback, Gavel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const IconMap: Record<string, React.ElementType> = {
  'summary-icon': Description,
  'report-icon': Assessment,
  'department-icon': People,
  'ops-icon': Settings,
  'experience-icon': Feedback,
  'governance-icon': Gavel,
};

interface SubCategory {
  label: string;
  icon?: string; // Name of the icon to be mapped
  route?: string; // Route for navigation
  subCategories?: SubCategory[]; // Infinite depth
  defaultOpen?: boolean; // Default open/closed state
}

interface CollapsibleMenuWidgetProps {
  module: string;
  subCategories: SubCategory[];
  defaultOpen?: boolean; // Default open/closed state for the top-level module
}

interface MenuItemProps {
  item: SubCategory;
  level: number;
  defaultOpen?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, level, defaultOpen = false }) => {
  const [open, setOpen] = useState(item.defaultOpen !== undefined ? item.defaultOpen : defaultOpen);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleClick = () => {
    if (item.subCategories) {
      setOpen(!open);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const CurrentIcon = item.icon ? IconMap[item.icon] : null;

  return (
    <>
      <ListItemButton onClick={handleClick} sx={{ 
        pl: level * 2,
        color: theme.palette.textLight.dark,
        // backgroundColor: theme.palette.backgroundLight.secondary,
        backgroundColor: theme.palette.backgroundLight.sidenav,
        // boxShadow: "0 1px 0 "+theme.palette.backgroundLight.sidenavBorder,
        // mb: 0.5,
        borderRadius: "0",
        fontSize: "1.1rem",
        fontWeight: 400 
      }}>
        {CurrentIcon && (
          <ListItemIcon>
            <CurrentIcon sx={{ color: theme.palette.textLight.dark }} />
          </ListItemIcon>
        )}
        <ListItemText primary={item.label} />
        {item.subCategories && (open 
          ? <ExpandLess sx={{color: theme.palette.textLight.dark}} /> 
          : <ExpandMore sx={{color: theme.palette.textLight.dark}} />)}
      </ListItemButton>
      {item.subCategories && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subCategories.map((subItem, index) => (
              <MenuItem key={`${index}-${subItem.label}`} item={subItem} level={level + 1} defaultOpen={defaultOpen} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const CollapsibleMenuWidget: React.FC<CollapsibleMenuWidgetProps> = ({ module, subCategories, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const theme = useTheme();

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 360, bgcolor: theme.palette.backgroundLight.sidenav, color: theme.palette.textLight.dark }}>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <Typography variant="h6" sx={{ color: theme.palette.textLight.dark }}>{module}</Typography>
        </ListItemIcon>
        {open 
        ? <ExpandLess sx={{color: theme.palette.textLight.dark}} /> 
        : <ExpandMore sx={{color: theme.palette.textLight.dark}} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="nav" sx={{ bgcolor: theme.palette.backgroundLight.sidenav }} disablePadding>
          {subCategories.map((item, index) => (
            <MenuItem key={`${index}-${item.label}`} item={item} level={1} defaultOpen={defaultOpen} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default CollapsibleMenuWidget;